/**
 * 모임 기록 섹션 — 작성기 + 남기기 시트를 한 덩어리로 묶습니다.
 *
 * 붙는 곳이 셋이라(홈 오늘 카드 · 일반 모임 상세 · 여행 상세) 글을 올리는
 * 절차를 각자 들고 있으면 금세 갈라집니다. 여기 한 곳에 둡니다.
 *
 * 올리는 절차는 기존 usePlacePhotoUpload 를 그대로 씁니다 —
 * 후기 한 건을 먼저 만들고 그 번호로 사진을 붙입니다.
 */
import React, { useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useAlert } from 'contexts/AlertContext';
import { usePlacePhotoUpload } from 'hooks/usePlacePhotoUpload';
import { JournalComposer } from './JournalComposer';
import { JournalSheet, JournalAnchor } from './JournalSheet';

interface Props {
    meetingId?: string;
    /** 기록이 붙을 장소 — 없으면 아무것도 그리지 않습니다 */
    anchor: JournalAnchor | null;
    profileUrl?: string | null;
    nickname?: string | null;
    /** 장소를 바꿀 수 있을 때만 넘깁니다 */
    onChangeAnchor?: () => void;
    /** 올리고 난 뒤 — 목록을 다시 불러야 하는 화면이 씁니다 */
    onPosted?: () => void;
}

export const MeetingJournalSection: React.FC<Props> = ({
    meetingId, anchor, profileUrl, nickname, onChangeAnchor, onPosted,
}) => {
    const { t } = useTranslation();
    const { showAlert } = useAlert();
    const [open, setOpen] = useState(false);
    const [initialPhotos, setInitialPhotos] = useState<File[]>([]);

    const { busy, submitVisitPost } = usePlacePhotoUpload({
        placeId: anchor?.placeId || '',
        placeName: anchor?.placeName || undefined,
        meetingId,
        dayNumber: anchor?.dayNumber ?? undefined,
        subId: anchor?.subId ?? undefined,
    });

    const openWithPhotos = useCallback((files: File[]) => {
        setInitialPhotos(files);
        setOpen(true);
    }, []);

    const openPlain = useCallback(() => {
        setInitialPhotos([]);
        setOpen(true);
    }, []);

    const handleSubmit = useCallback(async (draft: {
        content: string; rating: number; photos: File[];
    }) => {
        const res = await submitVisitPost({
            rating: draft.rating,
            content: draft.content,
            tags: [],
            photos: draft.photos,
        });

        if (!res.ok) {
            /* 서버가 이유를 주면 그대로 보여 줍니다 — "등록 실패" 만으로는
               무엇을 고쳐야 할지 알 수 없습니다. */
            showAlert({ message: (res as any).message || t('journal.submitFailed') });
            return;
        }

        setOpen(false);
        /* 글은 올라갔는데 사진 몇 장이 빠진 경우 — "실패" 라고 하면 다시 쓰게 됩니다 */
        if (res.photosFailed > 0) {
            showAlert({ message: t('journal.photosFailed', { count: res.photosFailed }) });
        }
        onPosted?.();
    }, [submitVisitPost, showAlert, t, onPosted]);

    if (!anchor?.placeId) return null;

    return (
        <>
            <JournalComposer
                placeName={anchor.placeName}
                profileUrl={profileUrl}
                nickname={nickname}
                onWrite={openPlain}
                onPickPhotos={openWithPhotos}
                disabled={busy}
            />
            <JournalSheet
                open={open}
                onClose={() => setOpen(false)}
                anchor={anchor}
                initialPhotos={initialPhotos}
                onChangeAnchor={onChangeAnchor}
                busy={busy}
                onSubmit={handleSubmit}
            />
        </>
    );
};
